import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@wundo/client/api.ts';
import { linkJourneyStep, unlinkJourneyStep } from '@wundo/client/userJourneys.ts';
import type { CalendarMeetingItem } from './meetings.ts';
import { m } from '@/paraglide/messages.js';

/* How long the confirmation — and the way back with it — stays on screen. */
const TOAST_FOR_MS = 5000;

interface Linked {
  journeyId: string;
  stepId: string;
}

export interface LinkJourneySuggestion {
  /** Link the meeting to the journey step Wundo suggested for it. Nothing is asked; the toast can undo it. */
  link: (meeting: CalendarMeetingItem) => void;
  /** Take the last link back. */
  undo: () => void;
  /** Close the toast, when another action on the page speaks next. */
  dismiss: () => void;
  message: string | null;
  undoable: boolean;
}

/**
 * The agenda's journey pill: one click links the meeting to the step Wundo was
 * confident about, with the same undo as adding a meeting, and the manager stays
 * on their week. The agenda does not choose the step; the suggestion does.
 */
export function useLinkJourneySuggestion(
  userId: string | undefined,
): LinkJourneySuggestion {
  const queryClient = useQueryClient();
  const [linked, setLinked] = useState<Linked | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [undoable, setUndoable] = useState(false);

  useEffect(() => {
    if (message === null) return;
    const timer = setTimeout(() => setMessage(null), TOAST_FOR_MS);
    return () => clearTimeout(timer);
  }, [message]);

  /* The card, the journey page and its list all read the link. */
  const refresh = (journeyId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['meetings'] }),
      queryClient.invalidateQueries({ queryKey: ['userJourney', userId, journeyId] }),
      queryClient.invalidateQueries({ queryKey: ['userJourneys', userId] }),
    ]);

  const toast = (text: string, canUndo: boolean) => {
    setMessage(text);
    setUndoable(canUndo);
  };

  const link = (meeting: CalendarMeetingItem) => {
    const suggestion = meeting.journey_suggestion;
    if (!suggestion || !userId) return;
    const target = { journeyId: suggestion.journey_id, stepId: suggestion.step_id };

    linkJourneyStep(userId, target.journeyId, target.stepId, meeting.id)
      .then(() => {
        setLinked(target);
        toast(m.journey_linked(), true);
      })
      .catch((failure: unknown) => {
        setLinked(null);
        const refused = failure instanceof ApiError && failure.type === 'validation';
        toast(refused ? m.journey_link_refused() : m.journey_link_failed(), false);
      })
      .finally(() => void refresh(target.journeyId));
  };

  const undo = () => {
    if (!linked || !userId) return;
    const target = linked;
    setLinked(null);
    setMessage(null);
    unlinkJourneyStep(userId, target.journeyId, target.stepId)
      .catch(() => toast(m.agenda_undo_failed(), false))
      .finally(() => void refresh(target.journeyId));
  };

  const dismiss = () => setMessage(null);

  return { link, undo, dismiss, message, undoable };
}
